import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { setMeetMode } from "@/Redux/Action/organizationActions";
import { CommanServices, normalizeIncidentRecipientDirectory } from "@/services/commanServices";
import axios from "axios";
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  Eye,
  FileCode,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Hash,
  Image as ImageIcon,
  Loader2,
  Lock,
  MapPin,
  MessageSquare,
  Paperclip,
  Phone,
  Play,
  Plus,
  Send,
  Settings,
  Users,
  Video,
  X,
  Zap
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import remarkGfm from 'remark-gfm';
import { ApiEndPoint, persistMeetModeForNextMeet } from "../services/apiUttiLities";
import { MAX_FILE_BYTES, MAX_VIDEO_BYTES } from "../utils/uploadLimits";
import socketService from "../services/socketService";
import ChannelManagerModal from "./ChannelManagerModal";
import { WEB_ACCESS, getWebAccessLevel, isWebAdmin } from "../auth/webAccess";
import AuthenticatedMedia, { AuthenticatedMediaLink } from "../components/AuthenticatedMedia";
import {
  buildChatWorkspaceBackUrl,
  resolveChatWorkspaceContext,
  scenarioAwareSystemChannel,
  shouldFetchChatIncidentDetail,
} from "./chatWorkspaceContext";

const readStoredWebUser = () => {
  try {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  } catch (_) {
    return null;
  }
};

const titleCase = (str) => {
  if (!str) return "";
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) return <FileText className="text-red-500" size={20} />;
  if (contentType?.includes('video/')) return <FileVideo className="text-purple-500" size={20} />;
  if (contentType?.includes('image/')) return <ImageIcon className="text-green-500" size={20} />;
  if (contentType?.includes('word')) return <FileText className="text-blue-500" size={20} />;
  if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return <FileSpreadsheet className="text-green-600" size={20} />;
  if (contentType?.includes('zip') || contentType?.includes('compressed')) return <FileIcon className="text-amber-500" size={20} />;
  if (contentType?.includes('javascript') || contentType?.includes('json') || contentType?.includes('html')) return <FileCode className="text-purple-500" size={20} />;
  return <FileIcon className="text-gray-500" size={20} />;
};

const getFullUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/uploads/")) {
    return `${ApiEndPoint}${url}`;
  }
  return url;
};

/** Standardizes location display across the app (Matches Dashboard.jsx logic) */
const getLocationDisplay = (incident) => {
  // First check if we have resolved location names from backend
  if (incident?.location_names && Array.isArray(incident.location_names) && incident.location_names.length > 0) {
    return incident.location_names.join(' > ');
  }

  // Fallback to location field
  const location = incident?.location || incident;

  // Handle LocationSelector array format (list of location IDs)
  if (Array.isArray(location)) {
    if (location.length === 0) return 'No location specified';
    return `${location.length} location(s) selected`;
  }

  // Handle legacy formats
  if (typeof location === 'string') return location;
  if (location?.display_label) return location.display_label;
  if (location?.location) return location.location;
  if (location?.address) return location.address;
  return 'Unknown Location';
};

const Chat = () => {
  const { id: incidentId } = useParams();
  const location = useLocation();
  const { incidents } = useSelector((state) => state.organization);
  const incidentFromStore = (incidents || []).find((inc) => inc.id === incidentId);
  const [incidentDetail, setIncidentDetail] = useState(null);
  const incident = incidentDetail || incidentFromStore;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const chatWorkspace = resolveChatWorkspaceContext({
    search: location.search,
    incident,
  });
  const {
    isScenario: isScenarioChat,
    scenarioId,
    chatLabel,
    participantLabel,
    workspaceLabel,
  } = chatWorkspace;
  const backToWorkspaceUrl = buildChatWorkspaceBackUrl({
    incidentId,
    isScenario: isScenarioChat,
    scenarioId,
  });

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCall, setActiveCall] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [canStartCall, setCanStartCall] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => isWebAdmin(readStoredWebUser()));
  const [participants, setParticipants] = useState([]);
  const [isParticipantsListOpen, setIsParticipantsListOpen] = useState(false);
  const [chatAccessDenied, setChatAccessDenied] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [chatLists, setChatLists] = useState([]); // DM/group conversations for the sidebar panel
  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [channelPasskey, setChannelPasskey] = useState("");
  const [unlockingChannel, setUnlockingChannel] = useState(false);
  const [channelUnlockError, setChannelUnlockError] = useState("");
  const [channelManager, setChannelManager] = useState({ open: false, channel: null, initialUserIds: [] });
  const [scopedContactUserIds, setScopedContactUserIds] = useState(null);
  // activeConvo = null → incident chat | { type:'dm'|'group', id, name, users?, roomId? }
  const [activeConvo, setActiveConvo] = useState(null);
  const [convoMessages, setConvoMessages] = useState([]);
  const [convoLoading, setConvoLoading] = useState(false);
  const fileInputRef = useRef(null);
  const navigatingRef = useRef(false);
  const scrollRef = useRef(null);
  const activeChannel = channels.find((channel) => String(channel.id) === String(activeChannelId)) || null;
  const channelLocked = Boolean(!activeConvo && activeChannel?.requires_passkey && !activeChannel?.is_unlocked);
  const canUseActiveChannel = Boolean(
    activeChannel && (activeChannel.can_access ?? ((activeChannel.is_member || activeChannel.can_manage) && !channelLocked))
  );
  const channelReadOnly = [
    "resolved", "closed", "completed", "cancelled", "canceled", "false_alarm", "false alarm",
  ].includes(String(incident?.status || "").trim().toLowerCase());
  const currentAccessLevel = getWebAccessLevel(currentUser);
  const currentUserIsLeader = currentAccessLevel === WEB_ACCESS.LEADER;
  const scopedContactSet = new Set((scopedContactUserIds || []).map(String));
  const scopedContactSignature = (scopedContactUserIds || []).map(String).sort().join('|');
  const canContactUser = (targetId) => {
    if (!targetId) return false;
    if (isAdmin || !currentUserIsLeader) return true;
    return scopedContactUserIds !== null && scopedContactSet.has(String(targetId));
  };
  const visibleActiveChannelMembers = (activeChannel?.members || []).filter((member) => (
    canContactUser(member?.id || member?.userId || member?._id)
  ));
  const activeChannelWithinContactScope = !currentUserIsLeader || (
    scopedContactUserIds !== null
    && (activeChannel?.members || []).every((member) => canContactUser(member?.id || member?.userId || member?._id))
  );

  useEffect(() => {
    const user = readStoredWebUser();
    if (user) {
      setCurrentUser(user);
      const accessLevel = getWebAccessLevel(user);
      const isAdminUser = accessLevel === WEB_ACCESS.ADMIN;
      const isLeader = accessLevel === WEB_ACCESS.LEADER;
      setIsAdmin(isAdminUser);

      let isInvolved = false;
      if (incident) {
        isInvolved = (
          user.id === incident.created_by ||
          (incident.assigned_users && incident.assigned_users.includes(user.id))
        );
      }
      setCanStartCall(isAdminUser || (!isLeader && isInvolved));
    }
  }, [incident?.id]);

  useEffect(() => {
    const shouldFetchDetail = shouldFetchChatIncidentDetail({
      incidentId,
      search: location.search,
      incidentFromStore,
    });
    if (!shouldFetchDetail) {
      setIncidentDetail(null);
      return;
    }
    let cancelled = false;
    setIncidentDetail(null);
    CommanServices.getIncidentDetail(incidentId)
      .then((response) => {
        if (cancelled) return;
        const detail = response?.data ?? response;
        if (detail?.id) setIncidentDetail(detail);
      })
      .catch((error) => {
        if (!cancelled) console.error("Failed to fetch incident chat context:", error);
      });
    return () => { cancelled = true; };
  }, [incidentId, incidentFromStore, location.search]);

  useEffect(() => {
    const user = readStoredWebUser();
    const userId = user?.id || user?._id;
    if (!incidentId || getWebAccessLevel(user) !== WEB_ACCESS.LEADER || !userId) {
      setScopedContactUserIds(null);
      return;
    }
    let cancelled = false;
    setScopedContactUserIds([]);
    CommanServices.getIncidentRecipientDirectory(incidentId)
      .then(normalizeIncidentRecipientDirectory)
      .then((directory) => {
        if (cancelled) return;
        const ids = [...new Set([...(directory.userIds || []), userId].filter(Boolean).map(String))];
        setScopedContactUserIds(ids);
        setCanStartCall(ids.some((id) => id !== String(userId)));
      })
      .catch(() => {
        if (!cancelled) {
          setScopedContactUserIds([]);
          setCanStartCall(false);
        }
      });
    return () => { cancelled = true; };
  }, [incidentId]);

  // Auto-switch to group/DM convo when navigated from Staff Modal with selected users
  useEffect(() => {
    const groupUsers = location.state?.groupChatUsers;
    if (!groupUsers || groupUsers.length === 0) return;
    window.history.replaceState({}, document.title);

    if (groupUsers.length === 1) {
      // Single → DM convo
      if (canContactUser(groupUsers[0].id)) {
        setActiveConvo({ type: 'dm', id: groupUsers[0].id, name: groupUsers[0].name });
      }
    } else if (isWebAdmin(readStoredWebUser())) {
      // Multi-user conversations are secure, persisted channels managed on web.
      setActiveConvo(null);
      setChannelManager({
        open: true,
        channel: null,
        initialUserIds: groupUsers.map((user) => String(user.id)).filter(Boolean),
      });
    }
  }, [location.state, incidentId, scopedContactSignature]);

  // Auto-switch back to group/DM convo when navigating back from meeting
  useEffect(() => {
    const restoreRoomId = location.state?.restoreConvoRoomId;
    if (!restoreRoomId) return;

    // Clear history state
    window.history.replaceState({}, document.title);

    const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
    const myId = storedUser?.id || storedUser?._id;

    if (restoreRoomId.startsWith("DM-")) {
      const otherId = restoreRoomId.replace(`DM-${incidentId}-`, "").split("_").find(id => String(id) !== String(myId));
      if (otherId) {
        setActiveConvo({ type: 'dm', id: otherId, roomId: restoreRoomId });
      }
    } else if (restoreRoomId.startsWith("CHANNEL-")) {
      setActiveConvo(null);
      setActiveChannelId(restoreRoomId.replace("CHANNEL-", ""));
    }
  }, [location.state, incidentId]);

  useEffect(() => {
    if (!loading) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  useEffect(() => {
    setActiveCall(null);
    if (!incidentId || incidentId === "null" || incidentId === "undefined") return;

    let isSubscribed = true;

    const unsubNew = socketService.on('new_call', (data) => {
      if (data.incidentId === incidentId && incidentId && incidentId !== "null") {
        setActiveCall(data);
      }
    });

    const unsubStatus = socketService.on('call_status_update', (data) => {
      if (incidentId && incidentId !== "null" && data.incidentId === incidentId) {
        if (['ended', 'missed', 'declined'].includes(data.status)) {
          setActiveCall(null);
        } else {
          setActiveCall(data);
        }
      }
    });

    const fetchActiveCall = async () => {
      try {
        const response = await axios.get(`${ApiEndPoint}/api/calls/active/incident/${incidentId}`, {
          params: activeChannelId && !activeConvo ? { channelId: activeChannelId } : {},
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (isSubscribed) {
          if (response.data?.data) {
            setActiveCall(response.data.data);
          } else {
            setActiveCall(null);
          }
        }
      } catch (error) {
        console.error("Failed to fetch active call:", error);
        if (isSubscribed) setActiveCall(null);
      }
    };

    fetchActiveCall();

    return () => {
      isSubscribed = false;
      unsubNew();
      unsubStatus();
    };
  }, [incidentId, activeChannelId, activeConvo?.type]);

  // Build the server-owned channel list followed by private 1:1 conversations.
  const buildChatLists = async () => {
    if (!incidentId) return;
    let channelRows = [];
    let directRows = [];
    try {
      const [channelRes, directRes] = await Promise.all([
        CommanServices.getIncidentChannels(incidentId),
        axios.get(`${ApiEndPoint}/api/dm/conversations`, {
          params: { incidentId },
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        }),
      ]);
      if (channelRes?.status) channelRows = channelRes.data || [];
      if (directRes.data?.status) {
        directRows = (directRes.data.data || []).filter((conversation) => !conversation.is_group && conversation.type !== 'group');
      }
    } catch (_) {}
    const presentedChannelRows = channelRows.map((channel) => (
      scenarioAwareSystemChannel(channel, isScenarioChat)
    ));
    setChannels(presentedChannelRows);
    setActiveChannelId((current) => {
      if (current && presentedChannelRows.some((channel) => String(channel.id) === String(current))) return current;
      return presentedChannelRows.find((channel) => channel.system_key === 'incident')?.id || presentedChannelRows[0]?.id || null;
    });
    setChatLists([
      ...presentedChannelRows.map((channel) => ({
        ...channel,
        _id: channel.id,
        type: 'channel',
        is_channel: true,
        lastMessage: channel.last_message || { text: channel.description || '' },
        unreadCount: channel.unread_count || 0,
      })),
      ...directRows,
    ]);
  };

  const unlockActiveChannel = async (event) => {
    event?.preventDefault?.();
    if (!activeChannelId || !channelPasskey || unlockingChannel) return;
    setUnlockingChannel(true);
    setChannelUnlockError("");
    try {
      const result = await CommanServices.unlockIncidentChannel(incidentId, activeChannelId, channelPasskey);
      if (!result?.status) throw new Error(result?.message || "Could not unlock this channel");
      setChannelPasskey("");
      await buildChatLists();
    } catch (unlockError) {
      setChannelUnlockError(unlockError?.message || "Incorrect channel passkey.");
    } finally {
      setUnlockingChannel(false);
    }
  };

  useEffect(() => {
    setChannelPasskey("");
    setChannelUnlockError("");
  }, [activeChannelId]);

  useEffect(() => {
    buildChatLists();
    const onRefresh = (payload) => {
      if (payload?.incidentId && String(payload.incidentId) !== String(incidentId)) return;
      buildChatLists();
    };
    socketService.on('new_dm_notification', onRefresh);
    socketService.on('incident_channels_changed', onRefresh);
    window.addEventListener('dm_message_received', onRefresh);
    return () => {
      socketService.off('new_dm_notification', onRefresh);
      socketService.off('incident_channels_changed', onRefresh);
      window.removeEventListener('dm_message_received', onRefresh);
    };
  }, [incidentId, incident?.id, isScenarioChat]);

  // When a new DM or group convo is activated, upsert it into chatLists immediately
  useEffect(() => {
    if (!activeConvo) return;
    setChatLists(prev => {
      const exists = prev.some(c => {
        if (activeConvo.type === 'dm') return c.type !== 'incident' && !c.is_group && (c.id || c._id) === activeConvo.id;
        if (activeConvo.type === 'group') return c.roomId === activeConvo.roomId;
        return false;
      });
      if (exists) return prev;
      // Build a local placeholder entry for the new convo
      const myId = currentUser?.id || currentUser?._id;
      const newEntry = activeConvo.type === 'group'
        ? {
            _id: activeConvo.roomId,
            _local: true,
            type: 'group',
            roomId: activeConvo.roomId,
            name: activeConvo.name,
            is_group: true,
            participants: (activeConvo.users || []),
            lastMessage: { text: 'Group chat' },
          }
        : {
            _id: activeConvo.id,
            _local: true,
            type: 'dm',
            name: activeConvo.name,
            is_group: false,
            participants: [
              { id: myId },
              { id: activeConvo.id, name: activeConvo.name },
            ],
            lastMessage: { text: 'Direct message' },
          };
      // Insert after incident chat if it exists as first item, otherwise just prepend
      const firstItem = prev[0]?.is_incident || prev[0]?.type === 'incident' ? prev[0] : null;
      const rest = firstItem ? prev.slice(1) : prev;
      return firstItem ? [firstItem, newEntry, ...rest] : [newEntry, ...rest];
    });
  }, [activeConvo?.type, activeConvo?.id, activeConvo?.roomId]);

  // Fetch messages for the active DM or group convo
  useEffect(() => {
    if (!activeConvo) { setConvoMessages([]); return; }
    if (
      currentUserIsLeader
      && (activeConvo.type !== 'dm' || !canContactUser(activeConvo.id))
    ) {
      setConvoMessages([]);
      setActiveConvo(null);
      return;
    }
    setConvoLoading(true);
    setConvoMessages([]);

    const myId = currentUser?.id || currentUser?._id;

    if (activeConvo?.type === 'dm') {
      // Backend expects: DM-{incidentId}-{sortedIds}
      const roomId = `DM-${incidentId}-` + [String(myId), String(activeConvo.id)].sort().join('_');
      const roomPayload = { incidentId, otherUserId: activeConvo.id, room: roomId };
      socketService.emit('join_dm', roomPayload);
      socketService.emit('mark_dm_read', roomPayload);
      // Fetch DM history (path param endpoint)
      axios.get(`${ApiEndPoint}/api/dm/${encodeURIComponent(roomId)}/messages`, {
        params: { incidentId, otherUserId: activeConvo.id },
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      }).then(res => {
        const data = res.data?.data || res.data;
        if (Array.isArray(data)) {
          setConvoMessages(data.map(m => ({
            ...m,
            id: m._id || m.id,
            senderId: m.senderId,
            senderName: m.senderName,
            text: m.text,
            createdAt: m.createdAt,
          })));
        }
      }).catch(() => {}).finally(() => setConvoLoading(false));

      // Socket listener for incoming DMs
      const onDm = (msg) => {
        if (msg.room === roomId || msg.roomId === roomId) {
          setConvoMessages(prev => {
            if (prev.some(m => String(m.id || m._id) === String(msg.id || msg._id))) return prev;
            
            // Deduplicate local optimistic messages
            const myId = currentUser?.id || currentUser?._id;
            const isMe = String(msg.senderId || msg.userId) === String(myId);
            if (isMe) {
              const optIndex = prev.findIndex(m => m._optimistic && m.text === msg.text);
              if (optIndex !== -1) {
                const next = [...prev];
                next.splice(optIndex, 1, { ...msg, id: msg._id || msg.id, createdAt: msg.createdAt || new Date().toISOString() });
                return next;
              }
            }

            return [...prev, { ...msg, id: msg._id || msg.id, createdAt: msg.createdAt || new Date().toISOString() }];
          });
        }
      };
      socketService.on('dm_message', onDm);
      socketService.on('new_dm_message', onDm);
      return () => {
        socketService.off('dm_message', onDm);
        socketService.off('new_dm_message', onDm);
        socketService.emit('leave_dm', roomPayload);
      };
    }

    if (activeConvo.type === 'group') {
      // Group rooms use GRP- prefix, stored in the same dm_messages collection
      const { roomId } = activeConvo;
      socketService.emit('join_group_room', { roomId, incidentId });

      // Fetch group message history (same endpoint, GRP- prefix passes the DM-starts-with check)
      axios.get(`${ApiEndPoint}/api/dm/${encodeURIComponent(roomId)}/messages`, {
        params: { incidentId },
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      }).then(res => {
        const data = res.data?.data || res.data;
        if (Array.isArray(data)) {
          setConvoMessages(data.map(m => ({
            ...m,
            id: m._id || m.id,
            senderId: m.senderId,
            senderName: m.senderName,
            text: m.text,
            createdAt: m.createdAt,
          })));
        }
      }).catch(() => {}).finally(() => setConvoLoading(false));

      const onGrp = (msg) => {
        if (msg.room === roomId || msg.roomId === roomId) {
          setConvoMessages(prev => {
            if (prev.some(m => String(m.id || m._id) === String(msg.id || msg._id))) return prev;

            // Deduplicate local optimistic messages
            const myId = currentUser?.id || currentUser?._id;
            const isMe = String(msg.senderId || msg.userId) === String(myId);
            if (isMe) {
              const optIndex = prev.findIndex(m => m._optimistic && m.text === msg.text);
              if (optIndex !== -1) {
                const next = [...prev];
                next.splice(optIndex, 1, { ...msg, id: msg._id || msg.id, createdAt: msg.createdAt || new Date().toISOString() });
                return next;
              }
            }

            return [...prev, { ...msg, id: msg._id || msg.id, createdAt: msg.createdAt || new Date().toISOString() }];
          });
        }
      };
      socketService.on('group_message', onGrp);
      socketService.on('dm_message', onGrp);
      return () => {
        socketService.off('group_message', onGrp);
        socketService.off('dm_message', onGrp);
        socketService.emit('leave_group_room', { roomId });
      };
    }
  }, [activeConvo?.type, activeConvo?.id, activeConvo?.roomId, currentUser?.id, incidentId, scopedContactSignature]);

  useEffect(() => {
    if (!incidentId || !activeChannelId || activeConvo) return;

    if (channelLocked) {
      setMessages([]);
      setParticipants(visibleActiveChannelMembers);
      setChatAccessDenied(false);
      setLoading(false);
      setActiveCall(null);
      return;
    }

    setLoading(true);
    setChatAccessDenied(false);
    setParticipants(visibleActiveChannelMembers);

    const fetchHistory = async () => {
      try {
        const res = await CommanServices.getIncidentChannelMessages(incidentId, activeChannelId, { limit: 100 });
        if (res?.status) {
          const normalized = (res.data || []).map(m => ({
            ...m,
            id: m._id || m.id,
            createdAt: m.createdAt ? { toDate: () => new Date(m.createdAt) } : null
          }));
          normalized.sort((a, b) => {
            const ta = a.createdAt?.toDate?.()?.getTime?.() ?? 0;
            const tb = b.createdAt?.toDate?.()?.getTime?.() ?? 0;
            return ta - tb;
          });
          setMessages(normalized);
        } else if (String(res?.message || '').toLowerCase().includes("member")) {
          setChatAccessDenied(true);
        }
      } catch (err) {
        console.error("Failed to fetch chat history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();

    const joinChannelRoom = () => {
      socketService.emit("join_channel", { incidentId, channelId: activeChannelId });
    };
    joinChannelRoom();
    const unsubReconnectJoin = socketService.on("connect", joinChannelRoom);

    const handleNewMessage = (msg) => {
      if (String(msg.incidentId ?? "") !== String(incidentId ?? "")) return;
      if (String(msg.channel_id || msg.channelId || "") !== String(activeChannelId)) return;
      const normalized = {
        ...msg,
        id: msg._id || msg.id,
        createdAt: msg.createdAt ? { toDate: () => new Date(msg.createdAt) } : { toDate: () => new Date() }
      };

      setMessages((prev) => {
        if (prev.some(m => !m._optimistic && String(m.id) === String(normalized.id))) return prev;
        const myId = currentUser?.id || currentUser?._id;
        if (String(normalized.userId ?? "") === String(myId ?? "")) {
          const optIndex = prev.findIndex(m => m._optimistic && m.text === normalized.text);
          if (optIndex !== -1) {
            const next = [...prev];
            next.splice(optIndex, 1, normalized);
            return next;
          }
        }
        const next = [...prev, normalized];
        next.sort((a, b) => {
          const ta = a.createdAt?.toDate?.()?.getTime?.() ?? 0;
          const tb = b.createdAt?.toDate?.()?.getTime?.() ?? 0;
          return ta - tb;
        });
        return next;
      });
    };
    socketService.on("channel_message", handleNewMessage);

    const handleReadReceipt = (data) => {
      if (String(data.incidentId ?? "") !== String(incidentId ?? "")) return;
      if (String(data.channelId ?? "") !== String(activeChannelId)) return;
      setMessages(prev => prev.map(m => {
        if (m.incidentId === data.incidentId && !m.readBy?.includes(data.userId)) {
          return { ...m, readBy: [...(m.readBy || []), data.userId] };
        }
        return m;
      }));
    };
    socketService.on("channel_read_receipt", handleReadReceipt);

    socketService.emit("mark_channel_read", { incidentId, channelId: activeChannelId });

    return () => {
      unsubReconnectJoin();
      socketService.off("channel_message", handleNewMessage);
      socketService.off("channel_read_receipt", handleReadReceipt);
      socketService.emit("leave_channel", { incidentId, channelId: activeChannelId });
    };
  }, [incidentId, activeChannelId, activeConvo?.type, activeChannel?.updated_at, activeChannel?.is_unlocked, channelLocked, currentUser?.id, scopedContactSignature]);

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    const text = newMessage.trim();
    if (!text || !currentUser || !incidentId) return;
    if (channelReadOnly) return;

    const myId = currentUser.id || currentUser._id;
    const myName = currentUser.full_name || currentUser.name || 'Anonymous';

    // Private 1:1 messages use their dedicated encrypted room.
    if (activeConvo) {
      if (activeConvo.type !== 'dm' || !canContactUser(activeConvo.id)) return;
      const optimistic = {
        id: `opt-${Date.now()}`,
        senderId: myId,
        senderName: myName,
        userId: myId,
        username: myName,
        text,
        createdAt: new Date().toISOString(),
        _optimistic: true,
      };
      setConvoMessages(prev => [...prev, optimistic]);
      setNewMessage('');

      if (activeConvo.type === 'dm') {
        // Use backend-compatible DM room ID: DM-{incidentId}-{sorted user IDs}
        const roomId = `DM-${incidentId}-` + [String(myId), String(activeConvo.id)].sort().join('_');
        const payload = {
          room: roomId,        // backend uses 'room' field
          roomId,              // keep for socket consumers
          senderId: myId,
          senderName: myName,
          receiverId: activeConvo.id,
          recipientId: activeConvo.id,
          text,
          incidentId,
          createdAt: new Date().toISOString(),
        };
        socketService.emit('send_dm_message', payload);
      }
      return;
    }

    if (!activeChannelId || !canUseActiveChannel) return;
    if (channelReadOnly) return;

    // Incident/custom/command channel message.
    try {
      const optimisticId = `opt-${Date.now()}`;
      const optimisticMsg = {
        id: optimisticId,
        _optimistic: true,
        incidentId,
        text,
        username: myName,
        userId: myId,
        createdAt: { toDate: () => new Date() },
        readBy: [myId]
      };
      setMessages(prev => [...prev, optimisticMsg]);

      socketService.emit('send_channel_message', {
        incidentId,
        channelId: activeChannelId,
        text,
      });
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message', err);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !incidentId) return;
    if (channelReadOnly) return;
    if (activeConvo && (activeConvo.type !== 'dm' || !canContactUser(activeConvo.id))) return;

    // Validation
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const isPDF = file.type === "application/pdf";

    if (!isImage && !isVideo && !isPDF) {
      alert("Unsupported file type. Only images, videos, and PDFs are allowed.");
      return;
    }

    // Unified size limits (video: 20MB, others: 20MB)
    if (isVideo && file.size > MAX_VIDEO_BYTES) {
      alert("Video is too large. Maximum allowed is 20 MB.");
      return;
    }
    if (!isVideo && file.size > MAX_FILE_BYTES) {
      alert("File is too large. Maximum allowed is 20 MB.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("files", file);
    const isDirectMessage = activeConvo?.type === 'dm';
    if (isDirectMessage) {
      formData.append("other_user_id", activeConvo.id);
      formData.append("incident_id", incidentId);
    } else if (activeChannelId) {
      formData.append("channel_id", activeChannelId);
    }

    try {
      const response = await axios.post(
        isDirectMessage
          ? `${ApiEndPoint}/api/chat/upload`
          : `${ApiEndPoint}/api/incidents/${incidentId}/chat/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data.status) {
        const fileMeta = response.data.data;
        // Send a socket message with the attachment
        let type = "FILE";
        if (file.type.startsWith("image/")) type = "IMAGE";
        else if (file.type.startsWith("video/")) type = "VIDEO";
        else if (file.type === "application/pdf") type = "PDF";

        const optimisticId = `opt-${Date.now()}`;
        const optimisticMsg = {
          id: optimisticId,
          _optimistic: true,
          incidentId,
          text: `Sent a ${type.toLowerCase()}: ${file.name}`,
          type,
          attachment: fileMeta,
          username: currentUser.full_name || "Anonymous",
          userId: currentUser.id || currentUser._id,
          createdAt: { toDate: () => new Date() },
          readBy: [currentUser.id || currentUser._id]
        };
        if (activeConvo?.type === 'dm') {
          const targetId = activeConvo.id;
          const roomId = `DM-${incidentId}-` + [String(currentUser.id || currentUser._id), String(targetId)].sort().join('_');
          setConvoMessages((previous) => [...previous, {
            ...optimisticMsg,
            senderId: currentUser.id || currentUser._id,
            senderName: currentUser.full_name || currentUser.name || 'You',
            createdAt: new Date().toISOString(),
          }]);
          socketService.emit("send_dm_message", {
            incidentId,
            room: roomId,
            receiverId: targetId,
            text: optimisticMsg.text,
            type,
            attachment: fileMeta,
          });
        } else if (activeChannelId) {
          setMessages(prev => [...prev, { ...optimisticMsg, channel_id: activeChannelId }]);
          socketService.emit("send_channel_message", {
            incidentId,
            channelId: activeChannelId,
            text: optimisticMsg.text,
            type,
            attachment: fileMeta,
          });
        }
      } else {
        console.error("Upload failed details:", response.data);
        alert("Upload failed: " + response.data.message);
      }
    } catch (err) {
      console.error("File upload error:", err);
      alert("Failed to upload file.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleStartCall = async (payload) => {
    if (!currentUser || isProcessing) return;
    setIsProcessing(true);
    try {
      const response = await axios.post(`${ApiEndPoint}/api/calls/create`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
      });
      if (response.data.status) {
        const callData = response.data.data;
        dispatch(setMeetMode(payload.callType));
        persistMeetModeForNextMeet(payload.callType);
        navigate(`/meets?meeting_id=${callData.roomName}&meetMode=${payload.callType}&incidentId=${incidentId}&callId=${callData.id}`);
      }
    } catch (error) {
      console.error("Call creation error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleJoinCall = () => {
    if (!activeCall || isProcessing) return;
    dispatch(setMeetMode(activeCall.callType));
    persistMeetModeForNextMeet(activeCall.callType);
    navigate(`/meets?meeting_id=${activeCall.roomName}&meetMode=${activeCall.callType}&incidentId=${incidentId}&callId=${activeCall.id}`);
  };

  const handleDirectCall = async (targetUser, mode) => {
    const currentId = currentUser?.id || currentUser?._id;
    const targetId = targetUser?.id || targetUser?.userId;
    if (!targetId || !currentId || isProcessing || navigatingRef.current) return;
    if (String(targetId) === String(currentId)) return;
    if (channelReadOnly || !canContactUser(targetId)) return;

    setIsProcessing(true);
    try {
      const response = await axios.post(`${ApiEndPoint}/api/calls/create`, {
        callType: mode,
        invitedUsers: [targetId],
        incidentId: incidentId
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      const data = response.data?.data || response.data;
      const roomName = data?.roomName || data?.room_name || response.data?.roomName;
      const callId = data?.id || data?._id || response.data?.id;
      if (!roomName) throw new Error("Room ID not found");
      navigatingRef.current = true;
      dispatch(setMeetMode(mode));
      persistMeetModeForNextMeet(mode);
      navigate(`/meets?meeting_id=${roomName}&meetMode=${mode}&incidentId=${incidentId}&callId=${callId}`);
    } catch (error) {
      console.error("Direct call error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const myId = currentUser?.id || currentUser?._id;
  const currentChatRoomId = activeConvo
    ? (activeConvo.roomId || `DM-${incidentId}-` + [String(myId), String(activeConvo.id)].sort().join('_'))
    : `CHANNEL-${activeChannelId}`;

  const isCurrentChatCallActive = activeCall && activeCall.roomName === currentChatRoomId;

  const startCallDirectly = (callType) => {
    if (!currentUser || isProcessing || !incidentId || channelReadOnly) return;
    const myId = currentUser.id || currentUser._id;
    
    let invitedUsers = [];
    if (activeConvo?.type === 'dm') {
      if (!canContactUser(activeConvo.id)) return;
      invitedUsers = [activeConvo.id];
    }
    
    const payload = {
      incidentId,
      channelId: activeConvo ? undefined : activeChannelId,
      callType,
      invitedUsers,
      roomName: currentChatRoomId
    };
    
    handleStartCall(payload);
  };

  return (
    <div className="flex-1 flex bg-gray-100 min-h-0 overflow-hidden">
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden relative">
        {/* Header - Unified with Incident Detail Design */}
        <div className="bg-gradient-to-r from-gray-900 to-blue-900 border-b border-blue-800 px-6 py-4 flex-shrink-0 shadow-md relative z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(backToWorkspaceUrl)}
                className="p-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white/70 hover:text-white transition-all border border-white/10"
                title={isScenarioChat ? "Back to Scenario" : "Back to Details"}
              >
                <ArrowLeft size={18} />
              </button>
              <div className="flex flex-col">
                {activeConvo ? (
                  // ── Active convo header ──
                  <>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setActiveConvo(null)}
                        className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
                      >
                        <ArrowLeft size={13} /> {chatLabel}
                      </button>
                    </div>
                    <h2 className="text-xl font-bold text-white leading-tight mt-0.5 flex items-center gap-2">
                      {activeConvo.type === 'group'
                        ? <><Users size={18} className="text-violet-300" /> {activeConvo.name}</>  
                        : <><MessageSquare size={16} className="text-indigo-300" /> {activeConvo.name}</>}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                        activeConvo.type === 'group'
                          ? 'bg-violet-500/20 text-violet-300 border-violet-400/20'
                          : 'bg-indigo-500/20 text-indigo-300 border-indigo-400/20'
                      }`}>
                        {activeConvo.type === 'group' ? 'Group Chat' : 'Direct Message'}
                      </span>
                    </div>
                  </>
                ) : (
                  // ── Workspace channel header ──
                  <>
                    <h2 className="text-xl font-bold text-white leading-tight flex items-center gap-2">
                      <span className="opacity-50 font-medium">{workspaceLabel}:</span>
                      {isScenarioChat
                        ? (incident?.scenario_name || incident?.title || incident?.incident_type || scenarioId || "Scenario")
                        : (incident?.id
                          ? `INC-${incident.id.split('-').pop().substring(0, 8).toUpperCase()}`
                          : (incidentId?.split('-').pop()?.toUpperCase() || "Unknown"))}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      {activeChannel && (
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-[10px] font-bold uppercase tracking-wider border border-blue-400/20">
                          # {activeChannel.name}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5 text-xs text-gray-400">
                          <span className={`${incident ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-gray-500'} w-2 h-2 rounded-full`}></span>
                        {activeChannel?.member_count ?? participants.length} {isScenarioChat ? 'participant' : 'member'}{(activeChannel?.member_count ?? participants.length) === 1 ? '' : 's'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div 
                className="flex -space-x-2 mr-4 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setIsParticipantsListOpen(true)}
              >
                {participants.slice(0, 3).map((p, i) => {
                  const displayName = p.name || p.username || p.full_name || 'User';
                  const initial = (displayName[0] || 'U').toUpperCase();
                  return (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-gray-900 bg-indigo-600 flex items-center justify-center text-[10px] font-bold text-white shadow-lg" title={displayName}>
                      {initial}
                    </div>
                  );
                })}
                {participants.length > 3 && (
                  <div className="w-8 h-8 rounded-full border-2 border-gray-900 bg-gray-700 flex items-center justify-center text-[10px] font-bold text-white shadow-lg">
                    +{participants.length - 3}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {isCurrentChatCallActive ? (
                  <Button
                    className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white gap-2 px-5 rounded-full shadow-lg shadow-emerald-500/20 font-bold border-none animate-pulse"
                    onClick={handleJoinCall}
                  >
                    <Video size={18} />
                    <span>Join Active Call</span>
                  </Button>
                ) : (
                  !channelReadOnly && activeChannelWithinContactScope && (canStartCall || canUseActiveChannel || activeConvo) && (
                    <div className="flex items-center gap-2">
                      {isAdmin && (
                        <>
                          <Button
                            onClick={() => setChannelManager({ open: true, channel: null, initialUserIds: [] })}
                            className="h-10 bg-white/10 hover:bg-white/20 text-white gap-2 px-4 rounded-full border border-white/20 font-medium"
                          >
                            <Plus size={17} />
                            <span className="hidden sm:inline">Channel</span>
                          </Button>
                          {activeChannel ? (
                            <Button
                              onClick={() => setChannelManager({ open: true, channel: activeChannel, initialUserIds: [] })}
                              className="h-10 w-10 p-0 bg-white/10 hover:bg-white/20 text-white rounded-full border border-white/20"
                              title={`Manage ${activeChannel.name}`}
                            >
                              <Settings size={17} />
                            </Button>
                          ) : null}
                        </>
                      )}
                      <Button
                        onClick={() => {
                          if (activeConvo) {
                            startCallDirectly("voice");
                          } else {
                            startCallDirectly("voice");
                          }
                        }}
                        className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white gap-2 px-4 rounded-full shadow-lg shadow-emerald-500/20 font-bold border-none"
                        title="Start voice call"
                      >
                        <Phone size={18} />
                        <span className="hidden sm:inline">Voice</span>
                      </Button>
                      <Button
                        onClick={() => {
                          if (activeConvo) {
                            startCallDirectly("video");
                          } else {
                            startCallDirectly("video");
                          }
                        }}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2 px-4 rounded-full shadow-lg shadow-indigo-500/20 h-10 font-bold border-none"
                        title="Start video call"
                      >
                        <Video size={18} />
                        <span className="hidden sm:inline">Video</span>
                      </Button>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contextual workspace status bar */}
        {incident && (
          <div className="bg-white px-6 py-2.5 border-b border-l flex-shrink-0 flex items-center gap-8 text-[11px] font-semibold shadow-sm z-10 overflow-x-auto no-scrollbar whitespace-nowrap">
            {/* Severity Section */}
            <div className="flex items-center gap-2 pr-6 border-r border-gray-100">
              <span className="text-gray-400 uppercase tracking-widest text-[9px]">Severity</span>
              {(() => {
                const sev = String(incident.severity || 'Medium').toLowerCase();
                let colors = "bg-gray-100 text-gray-700 border-gray-200";
                let iconColor = "text-gray-500";
                
                if (sev.includes('high') || sev.includes('critical')) {
                  colors = "bg-rose-50 text-rose-700 border-rose-100";
                  iconColor = "text-rose-500";
                } else if (sev.includes('medium') || sev.includes('moderate')) {
                  colors = "bg-orange-50 text-orange-700 border-orange-100";
                  iconColor = "text-orange-500";
                } else if (sev.includes('low')) {
                  colors = "bg-yellow-50 text-yellow-700 border-yellow-100";
                  iconColor = "text-yellow-600";
                }
                
                return (
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${colors} shadow-sm`}>
                    <AlertTriangle size={12} className={iconColor} />
                    <span className="uppercase tracking-wide">{incident.severity || 'Medium'}</span>
                  </div>
                );
              })()}
            </div>

            {/* Type Section */}
            <div className="flex items-center gap-2 pr-6 border-r border-gray-100">
              <span className="text-gray-400 uppercase tracking-widest text-[9px]">Category</span>
              <div className="flex items-center gap-1.5 text-gray-700 bg-blue-50/50 px-2.5 py-1 rounded-full border border-blue-100/50">
                <Zap size={12} className="text-blue-500" />
                <span>{incident.incident_type || 'General'}</span>
              </div>
            </div>

            {/* Location Section */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-gray-400 uppercase tracking-widest text-[9px]">Location</span>
              <div className="flex items-center gap-1.5 text-gray-700 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-100 min-w-0">
                <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                <span className="truncate italic">
                  {getLocationDisplay(incident)}
                </span>
              </div>
            </div>

            {/* Context Badge */}
            <div className="hidden lg:flex items-center gap-2 text-[9px] text-indigo-400 bg-indigo-50/50 px-2.5 py-1 rounded-full uppercase font-bold tracking-tighter border border-indigo-100/50">
              <div className="w-1 h-1 bg-indigo-400 rounded-full animate-pulse"></div>
              {isScenarioChat ? 'Scenario Context' : 'Real-time Context'}
            </div>
          </div>
        )}

        {/* Messages — switches between incident chat and active DM/group convo */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50">
          {/* ── Active convo messages ── */}
          {activeConvo ? (
            convoLoading ? (
              <div className="flex justify-center items-center h-full">
                <svg className="animate-spin h-8 w-8 text-indigo-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </div>
            ) : convoMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <MessageSquare className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-sm font-medium">No messages yet</p>
                <p className="text-xs mt-1">Send the first message to start the conversation</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {convoMessages.map((msg, i) => {
                  const myId = currentUser?.id || currentUser?._id;
                  const isMe = String(msg.senderId || msg.userId) === String(myId);
                  const name = msg.senderName || msg.username || 'Member';
                  const timeStr = msg.createdAt
                    ? new Date(typeof msg.createdAt === 'string' ? msg.createdAt : msg.createdAt?.toDate?.() || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '';

                  const isSystem = msg.userId === 'system' || msg.senderId === 'system' || msg.type === 'SYSTEM' || msg.isSystem;
                  if (isSystem) {
                    let displayMessage = msg.text || "";
                    if (msg.actorId === myId) {
                      const actorName = currentUser.full_name || currentUser.name || "dev";
                      if (displayMessage.toLowerCase().includes("missed call from")) {
                        displayMessage = "📞 Your call was not answered";
                      } else if (displayMessage.toLowerCase().includes("call declined by")) {
                        displayMessage = "📞 Your call was declined";
                      } else {
                        displayMessage = displayMessage.replace(new RegExp(actorName, "g"), "You");
                      }
                    }
                    return (
                      <div key={msg.id || i} className="flex justify-center my-2 w-full">
                        <div className="bg-white/50 border border-gray-100 px-4 py-1.5 rounded-full text-[11px] text-gray-500 flex items-center gap-2 shadow-sm">
                          <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                          <span>{displayMessage}</span>
                          <span className="text-[9px] opacity-60">{timeStr}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id || i} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                      <div className={`flex items-center gap-2 mb-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm ${
                          isMe ? 'bg-indigo-600' : activeConvo.type === 'group' ? 'bg-violet-500' : 'bg-amber-500'
                        }`}>
                          {(name[0] || 'U').toUpperCase()}
                        </div>
                        <span className="text-xs font-semibold text-gray-700">{isMe ? 'You' : name}</span>
                      </div>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm text-sm ${
                        isMe ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none'
                      }`}>
                        {msg.attachment ? (
                          <AuthenticatedMediaLink
                            href={getFullUrl(msg.attachment.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`mb-2 flex items-center gap-2 rounded-xl p-2 ${isMe ? 'bg-white/15 text-white' : 'bg-slate-50 text-slate-700'}`}
                          >
                            {msg.type === 'IMAGE' ? (
                              <AuthenticatedMedia as="img" src={getFullUrl(msg.attachment.url)} alt="" className="h-20 w-28 rounded-lg object-cover" />
                            ) : (
                              getFileIcon(msg.attachment.content_type || msg.attachment.filetype)
                            )}
                            <span className="max-w-44 truncate text-xs font-semibold">{msg.attachment.filename || msg.attachment.name || 'Attachment'}</span>
                          </AuthenticatedMediaLink>
                        ) : null}
                        {msg.text ? <p className="leading-relaxed">{msg.text}</p> : null}
                      </div>
                      <span className="text-[10px] text-gray-400 mt-1 px-1">{timeStr}</span>
                    </div>
                  );
                })}
                <div ref={scrollRef} />
              </div>
            )
          ) : (
          /* ── Incident chat messages (original) ── */
          loading ? (

            <div className="flex justify-center items-center h-full">
              <svg className="animate-spin h-8 w-8 text-indigo-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : channelLocked ? (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-indigo-50 shadow-sm">
                <Lock className="h-9 w-9 text-indigo-600" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-gray-900">Passkey required</h3>
              <p className="mt-1 max-w-sm text-sm text-gray-500">
                Enter the passkey provided by the administrator to join {activeChannel?.name || "this channel"}.
              </p>
              <form onSubmit={unlockActiveChannel} className="mt-5 flex w-full max-w-sm gap-2">
                <input
                  type="password"
                  value={channelPasskey}
                  onChange={(event) => { setChannelPasskey(event.target.value); setChannelUnlockError(""); }}
                  minLength={6}
                  maxLength={128}
                  autoComplete="current-password"
                  autoFocus
                  placeholder="Channel passkey"
                  className="min-w-0 flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
                <Button type="submit" disabled={unlockingChannel || channelPasskey.length < 6} className="rounded-xl bg-indigo-600 px-5 text-white">
                  {unlockingChannel ? <Loader2 size={17} className="animate-spin" /> : "Join"}
                </Button>
              </form>
              {channelUnlockError ? <p className="mt-3 text-sm font-medium text-rose-600">{channelUnlockError}</p> : null}
            </div>
          ) : chatAccessDenied || !canUseActiveChannel ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4 px-12 text-center">
              <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center border-4 border-white shadow-sm">
                <Lock className="w-10 h-10 text-amber-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Restricted Access</h3>
              <p className="text-gray-500 max-w-md">
                You are not a {isScenarioChat ? 'participant in this scenario chat' : 'member of this incident chat'}.
              </p>
              {isAdmin && activeChannel && (
                <Button
                  className="bg-indigo-600 text-white rounded-full"
                  onClick={() => setChannelManager({ open: true, channel: activeChannel, initialUserIds: [] })}
                >
                  Manage Channel
                </Button>
              )}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Users className="w-12 h-12 mb-3 opacity-20" />
              <p>No messages yet.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {(() => {
                const groups = [];
                let lastDate = null;
                messages.forEach((msg) => {
                  const date = msg.createdAt?.toDate?.() || new Date();
                  const dateStr = date.toDateString();
                  if (dateStr !== lastDate) {
                    const today = new Date().toDateString();
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayStr = yesterday.toDateString();
                    let label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    if (dateStr === today) label = "Today";
                    else if (dateStr === yesterdayStr) label = "Yesterday";
                    groups.push({ type: 'date', label });
                    lastDate = dateStr;
                  }
                  groups.push({ type: 'message', msg });
                });

                return groups.map((item, idx) => {
                  if (item.type === 'date') {
                    return (
                      <div key={`date-${idx}`} className="flex justify-center my-4">
                        <span className="px-3 py-1 bg-gray-200 text-gray-500 text-[10px] font-bold uppercase rounded-full shadow-sm">
                          {item.label}
                        </span>
                      </div>
                    );
                  }

                  const msg = item.msg;
                  const isSelf = msg.userId === (currentUser?.id || currentUser?._id);
                  const isSystem = msg.userId === 'system' || msg.type === 'system';
                  const date = msg.createdAt?.toDate?.() || new Date();
                  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                  if (isSystem) {
                    let displayMessage = msg.text || "";
                    if (msg.actorId === currentUser?.id) {
                      const actorName = currentUser.full_name || currentUser.name || "dev";
                      if (displayMessage.toLowerCase().includes("missed call from")) {
                        displayMessage = "📞 Your call was not answered";
                      } else if (displayMessage.toLowerCase().includes("call declined by")) {
                        displayMessage = "📞 Your call was declined";
                      } else {
                        displayMessage = displayMessage.replace(new RegExp(actorName, "g"), "You");
                      }
                    }
                    return (
                      <div key={msg.id || idx} className="flex justify-center my-2">
                        <div className="bg-white/50 border border-gray-100 px-4 py-1.5 rounded-full text-[11px] text-gray-500 flex items-center gap-2 shadow-sm">
                          <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                          <span>{displayMessage}</span>
                          <span className="text-[9px] opacity-60">{timeStr}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id || idx} className={`flex flex-col ${isSelf ? 'items-end' : 'items-start'}`}>
                      <div className={`flex items-center gap-2 mb-1 ${isSelf ? 'flex-row-reverse' : ''}`}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild disabled={isSelf}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm cursor-pointer ${isSelf ? 'bg-indigo-600' : 'bg-amber-500'}`}>
                              {(msg.username || 'U')[0].toUpperCase()}
                            </div>
                          </DropdownMenuTrigger>
                          {!isSelf && (
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem onClick={() => handleDirectCall({ id: msg.userId, name: msg.username }, 'voice')}>
                                <Phone className="mr-2 h-4 w-4" /> Voice Call
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDirectCall({ id: msg.userId, name: msg.username }, 'video')}>
                                <Video className="mr-2 h-4 w-4" /> Video Call
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          )}
                        </DropdownMenu>
                        <span className="text-xs font-semibold text-gray-700">{isSelf ? "You" : msg.username}</span>
                      </div>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm text-sm ${isSelf ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none'}`}>
                        {msg.type === "IMAGE" && msg.attachment ? (
                          <div className="space-y-2">
                            <AuthenticatedMediaLink href={getFullUrl(msg.attachment.url)} target="_blank" rel="noopener noreferrer">
                              <AuthenticatedMedia
                                as="img"
                                src={getFullUrl(msg.attachment.url)}
                                alt={msg.attachment.filename}
                                className="rounded-lg max-h-60 w-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                              />
                            </AuthenticatedMediaLink>
                            <p className={`text-[10px] italic ${isSelf ? 'text-indigo-100' : 'text-gray-400'}`}>{msg.attachment.filename}</p>
                          </div>
                        ) : msg.type === "VIDEO" && msg.attachment ? (
                          <div className="space-y-2 min-w-[200px]">
                            <AuthenticatedMedia
                              as="video"
                              src={getFullUrl(msg.attachment.url)}
                              controls
                              className="rounded-lg w-full max-h-60 bg-black"
                            />
                            <div className="flex items-center justify-between">
                              <p className={`text-[10px] italic truncate flex-1 mr-2 ${isSelf ? 'text-indigo-100' : 'text-gray-400'}`}>{msg.attachment.filename}</p>
                              <span className="text-[9px] opacity-60">{(msg.attachment.size / (1024 * 1024)).toFixed(1)} MB</span>
                            </div>
                          </div>
                        ) : (msg.type === "PDF" || msg.type === "FILE") && msg.attachment ? (
                          <div className={`flex flex-col gap-2 min-w-[200px]`}>
                            <div className={`flex items-center gap-3 p-2 rounded-lg ${isSelf ? 'bg-white/10' : 'bg-gray-100'}`}>
                              <div className={`p-2 rounded-lg ${isSelf ? 'bg-white/20' : 'bg-white'}`}>
                                {getFileIcon(msg.attachment.content_type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{msg.attachment.filename}</p>
                                <p className={`text-[10px] ${isSelf ? 'text-indigo-100' : 'text-gray-400'}`}>
                                  {msg.attachment.size ? `${(msg.attachment.size / 1024).toFixed(1)} KB` : "PDF Document"}
                                </p>
                              </div>
                              <AuthenticatedMediaLink
                                href={getFullUrl(msg.attachment.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`p-2 rounded-full hover:bg-black/5 transition-colors ${isSelf ? 'text-white' : 'text-indigo-600'}`}
                              >
                                <Download size={16} />
                              </AuthenticatedMediaLink>
                            </div>
                            {(msg.type === "PDF" || msg.attachment.content_type === "application/pdf") && (
                              <AuthenticatedMediaLink
                                href={getFullUrl(msg.attachment.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-full text-[10px] h-7 gap-1.5 ${isSelf ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-100 text-indigo-600'}`}
                              >
                                <Eye size={12} /> Preview PDF
                              </AuthenticatedMediaLink>
                            )}
                          </div>
                        ) : msg.type === "VOICE_CALL" ? (
                          <div
                            className="flex gap-2 cursor-pointer items-center"
                            onClick={() => {
                              persistMeetModeForNextMeet("voice");
                              navigate(
                                `/meets?incidentId=${incidentId}&meeting_id=${encodeURIComponent(msg?.meeting_id || "")}&meetMode=voice`
                              );
                            }}
                          >
                            <Phone size={16} className={isSelf ? "text-white" : "text-emerald-600"} />
                            <span className="font-medium underline decoration-dotted">Join voice call</span>
                          </div>
                        ) : msg.type === "VIDEO_CALL" ? (
                          <div
                            className="flex gap-2 cursor-pointer items-center"
                            onClick={() => {
                              persistMeetModeForNextMeet("video");
                              navigate(
                                `/meets?incidentId=${incidentId}&meeting_id=${encodeURIComponent(msg?.meeting_id || "")}&meetMode=video`
                              );
                            }}
                          >
                            <Video size={16} className={isSelf ? "text-white" : "text-indigo-600"} />
                            <span className="font-medium underline decoration-dotted">Join video session</span>
                          </div>
                        ) : (
                          <div className={`prose prose-sm max-w-none ${isSelf ? 'prose-invert' : ''}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 px-1">
                        <span className="text-[10px] text-gray-400">{timeStr}</span>
                        {isSelf && (
                          <span className={`text-[10px] font-bold ${msg.readBy?.length > 1 ? 'text-indigo-500' : 'text-gray-400'}`}>
                            {msg._optimistic ? "Sending..." : (msg.readBy?.length > 1 ? '✓✓ Seen' : '✓ Sent')}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
              <div ref={scrollRef} />
            </div>
          )
          /* end of incident-chat branch */
          )}
        </div>

        {/* Input Box */}
        <div className={`bg-white px-6 py-4 border-t relative flex-shrink-0 ${channelLocked && !activeConvo ? "hidden" : ""}`}>
          {isUploading && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex items-center gap-3">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*,video/*,application/pdf"
              onChange={handleFileSelect}
            />
            {/* Encrypted attachments are supported by channels and private 1:1 chat. */}
            {(
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={channelReadOnly || isUploading || chatAccessDenied || (!activeConvo && !canUseActiveChannel)}
                className="rounded-full shrink-0 h-10 w-10 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <Loader2 size={20} className="animate-spin text-indigo-500" />
                ) : (
                  <Paperclip size={20} />
                )}
              </Button>
            )}
            <input
              type="text"
              placeholder={
                channelReadOnly
                  ? `Completed ${isScenarioChat ? 'scenario' : 'incident'} — chat is read-only`
                  : activeConvo
                  ? `Message ${activeConvo.name}…`
                  : (chatAccessDenied || !canUseActiveChannel
                    ? 'Access restricted...'
                    : (channelReadOnly
                      ? `Resolved ${isScenarioChat ? 'scenario' : 'incident'} — channel is read-only`
                      : (isUploading ? `Uploading... ${uploadProgress}%` : 'Type a message...')))
              }
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={channelReadOnly || (!activeConvo && (isUploading || chatAccessDenied || !canUseActiveChannel))}
              className="flex-1 px-4 py-3 bg-gray-100 border-2 border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all disabled:opacity-60"
            />
            <Button
              type="submit"
              disabled={channelReadOnly || !newMessage.trim() || (!activeConvo && (isUploading || chatAccessDenied || !canUseActiveChannel))}
              className="rounded-full h-11 w-11 p-0 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 shadow-md transition-all shrink-0"
            >
              <Send size={18} className="text-white ml-0.5" />
            </Button>
          </form>
        </div>
      </div>

      {/* Sidebar - Split: Media & Files + Chat Lists */}
      <div className="w-80 bg-white border-l border-gray-200 flex flex-col min-h-0 overflow-hidden flex-shrink-0">

        {/* ── Top Section: Media & Gallery ── */}
        <div className="flex flex-col min-h-0 flex-1">
          <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Paperclip size={15} className="text-indigo-600" />
              Media &amp; Files
            </h2>
            <p className="text-[9px] text-gray-400 mt-0.5 uppercase tracking-wider font-bold">Conversation Gallery</p>
          </div>

          <div className="overflow-y-auto p-3 space-y-3" style={{ flex: '1 1 0' }}>
            {(() => {
              const chatMedia = (activeConvo ? convoMessages : messages)
                .filter(m => m.attachment)
                .map(m => ({
                  id: m.id || m._id,
                  name: m.attachment.filename,
                  url: m.attachment.url,
                  type: m.type,
                  size: m.attachment.size,
                  contentType: m.attachment.content_type,
                  timestamp: m.createdAt?.toDate?.() || new Date()
                }))
                .sort((a, b) => b.timestamp - a.timestamp);

              if (chatMedia.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center h-28 text-gray-400 border-2 border-dashed rounded-xl">
                    <FileIcon size={26} className="opacity-20 mb-1.5" />
                    <p className="text-[11px]">No media shared yet</p>
                  </div>
                );
              }

              return chatMedia.map((file, i) => (
                <div key={file.id || i} className="group relative bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                  {file.type === "IMAGE" ? (
                    <div className="h-24 w-full bg-gray-50 relative overflow-hidden">
                      <AuthenticatedMedia as="img" src={getFullUrl(file.url)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt="" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <AuthenticatedMediaLink href={getFullUrl(file.url)} target="_blank" rel="noopener noreferrer" className="p-2 bg-white rounded-full text-gray-800 hover:scale-110 transition-transform">
                          <Eye size={14} />
                        </AuthenticatedMediaLink>
                      </div>
                    </div>
                  ) : file.type === "VIDEO" ? (
                    <div className="h-24 w-full bg-slate-900 flex items-center justify-center relative">
                      <Play size={26} className="text-white/50" />
                      <div className="absolute bottom-1.5 right-1.5 px-1 py-0.5 bg-black/60 rounded text-[8px] text-white font-mono">VIDEO</div>
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <AuthenticatedMediaLink href={getFullUrl(file.url)} target="_blank" rel="noopener noreferrer" className="p-2 bg-white rounded-full text-gray-800 hover:scale-110 transition-transform">
                          <Play size={14} fill="currentColor" />
                        </AuthenticatedMediaLink>
                      </div>
                    </div>
                  ) : (
                    <div className="h-16 w-full bg-gray-50 flex items-center justify-center border-b border-gray-100">
                      {getFileIcon(file.contentType)}
                    </div>
                  )}
                  <div className="p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[11px] text-gray-800 truncate" title={file.name}>{file.name}</p>
                        <p className="text-[9px] text-gray-500 mt-0.5 uppercase">
                          {file.size ? `${(file.size / (1024 * 1024) >= 1) ? (file.size / (1024 * 1024)).toFixed(1) + ' MB' : (file.size / 1024).toFixed(1) + ' KB'}` : "File"}
                          {" • "}{file.timestamp.toLocaleDateString([], { day: '2-digit', month: 'short' })}
                        </p>
                      </div>
                      <AuthenticatedMediaLink
                        href={getFullUrl(file.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-600 hover:text-white transition-colors"
                      >
                        <Download size={12} />
                      </AuthenticatedMediaLink>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="flex-shrink-0 border-t-2 border-gray-100" />

        {/* ── Bottom Section: Channels ── */}
        <div className="flex flex-col min-h-0 flex-1">
          <div className="px-4 pt-3 pb-2 flex-shrink-0 flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <MessageSquare size={15} className="text-indigo-600" />
              CHANNEL
            </h2>
          </div>

          <div className="overflow-y-auto flex-1 px-3 pb-4 space-y-1.5">
            {chatLists.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-28 text-gray-400 border-2 border-dashed rounded-xl mt-1">
                <MessageSquare size={24} className="opacity-20 mb-1.5" />
                <p className="text-[11px] text-center">No conversations yet.<br/>Start a chat from the staff list.</p>
              </div>
            ) : (
              chatLists.filter(Boolean).map((convo, i) => {
                const myId = currentUser?.id || currentUser?._id;
                const isChannel = convo.is_channel || convo.type === 'channel';
                const isIncident = isChannel && convo.system_key === 'incident';
                const isGroup = false;

                // Resolve display name
                const other = !isChannel
                  ? ((convo.participants || []).find(p => String(p.id || p._id) !== String(myId)) || (convo.otherId ? { id: convo.otherId, name: convo.otherName } : null))
                  : null;
                if (currentUserIsLeader && !isChannel && !canContactUser(other?.id || other?._id)) {
                  return null;
                }
                const displayName = isChannel
                  ? (convo.name || (isScenarioChat ? 'Scenario Channel' : 'Incident Channel'))
                  : (other?.name || other?.full_name || other?.username || convo.name || 'User');

                const displayInitial = (displayName[0] || 'I').toUpperCase();
                const lastMsg = convo.lastMessage?.text || convo.last_message?.text || '';
                const lastTime = convo.lastMessage?.createdAt || convo.last_message?.createdAt || convo.updatedAt;
                const unread = convo.unreadCount || 0;

                // Active state
                const convoRoomId = convo.roomId || convo.room || convo.id || convo._id;
                const convoId = other?.id || other?._id;
                const isActive = isChannel
                  ? activeConvo === null && String(activeChannelId) === String(convo.id)
                  : activeConvo?.type === 'dm' && (String(activeConvo.id) === String(convoId) || (activeConvo.roomId && activeConvo.roomId === convoRoomId));

                const avatarBg = isChannel
                  ? 'bg-gradient-to-br from-blue-600 to-indigo-700'
                  : 'bg-gradient-to-br from-indigo-500 to-blue-600';

                const formatRelTime = (ts) => {
                  if (!ts) return '';
                  const d = new Date(ts);
                  const now = new Date();
                  const diffMs = now - d;
                  const diffMins = Math.floor(diffMs / 60000);
                  if (diffMins < 1) return 'just now';
                  if (diffMins < 60) return `${diffMins}m`;
                  const diffHrs = Math.floor(diffMins / 60);
                  if (diffHrs < 24) return `${diffHrs}h`;
                  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
                };

                return (
                  <button
                    key={convo._id || convo.id || i}
                    onClick={() => {
                      if (isChannel) {
                        setActiveConvo(null);
                        setActiveChannelId(convo.id);
                      } else if (other && canContactUser(other.id || other._id)) {
                        setActiveConvo({ type: 'dm', id: other.id || other._id, name: displayName, roomId: convo.roomId || convo.room });
                      }
                    }}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200 group text-left border ${
                      isActive
                        ? 'bg-indigo-50 border-indigo-200 shadow-sm'
                        : 'border-transparent hover:bg-gray-50 hover:border-gray-100'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white ${avatarBg}`}>
                        {isChannel ? <Hash size={15} /> : displayInitial}
                      </div>
                      {unread > 0 && !isChannel && (
                        <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 rounded-full flex items-center justify-center">
                          <span className="text-[8px] text-white font-bold">{unread > 9 ? '9+' : unread}</span>
                        </div>
                      )}
                      {isChannel && isActive && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className={`font-semibold text-xs truncate ${isActive ? 'text-indigo-700' : 'text-gray-800'}`}>
                          {displayName}
                        </span>
                        {isChannel && unread > 0 ? (
                          <span
                            className="h-2 w-2 flex-shrink-0 rounded-full bg-rose-500 shadow-sm shadow-rose-300"
                            aria-label={`${unread} unread message${unread === 1 ? '' : 's'}`}
                          />
                        ) : null}
                        {isChannel && convo.requires_passkey ? (
                          <Lock size={11} className={convo.is_unlocked ? "text-emerald-500" : "text-amber-500"} />
                        ) : null}
                        {lastTime && (
                          <span className="text-[9px] text-gray-400 flex-shrink-0">{formatRelTime(lastTime)}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wide ${
                          isIncident
                            ? 'bg-blue-100 text-blue-700'
                            : isGroup
                              ? 'bg-violet-100 text-violet-700'
                              : 'bg-sky-100 text-sky-700'
                        }`}>
                          {isChannel
                            ? (convo.system_key === 'command' ? (isScenarioChat ? 'Planning' : 'Command') : 'Channel')
                            : 'Direct'}
                        </span>
                        {lastMsg && (
                          <span className="text-[10px] text-gray-400 truncate">{lastMsg}</span>
                        )}
                      </div>
                    </div>

                    {/* Chat icon reveal */}
                    <MessageSquare size={14} className="text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {isAdmin && (
        <ChannelManagerModal
          isAdmin={isAdmin}
          isOpen={channelManager.open}
          onClose={() => setChannelManager({ open: false, channel: null, initialUserIds: [] })}
          incidentId={incidentId}
          channel={channelManager.channel}
          initialUserIds={channelManager.initialUserIds}
          isScenario={isScenarioChat}
          onSaved={buildChatLists}
        />
      )}

      {/* Participants List Modal — context-aware */}
      <Dialog open={isParticipantsListOpen} onOpenChange={setIsParticipantsListOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Users className="text-indigo-600" />
              {activeConvo?.type === 'dm'
                ? 'Direct Message'
                : activeConvo?.type === 'group'
                  ? 'Group Members'
                  : participantLabel}
              <span className="ml-auto bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full text-xs font-bold">
                {activeConvo?.type === 'dm'
                  ? 2
                  : activeConvo?.type === 'group'
                    ? (activeConvo.users?.length || 0) + 1
                    : participants.length}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-4 py-4">
            {(() => {
              const currentUserId = String(currentUser?.id || currentUser?._id || "");
              const myName = currentUser?.full_name || currentUser?.name || 'You';

              // Build context-aware participant list
              let listItems = [];

              if (activeConvo?.type === 'dm') {
                // Show current user + DM partner
                listItems = [
                  { id: currentUserId, name: myName, isMe: true },
                  { id: activeConvo.id, name: activeConvo.name, isMe: false },
                ];
              } else if (activeConvo?.type === 'group') {
                // Show current user + all group members
                listItems = [
                  { id: currentUserId, name: myName, isMe: true },
                  ...(activeConvo.users || []).map(u => ({
                    id: String(u.id || u._id || ''),
                    name: u.name || u.full_name || u.username || 'Member',
                    role: u.role || u.access_level,
                    profile_image: u.profile_image,
                    isMe: String(u.id || u._id) === currentUserId,
                  })).filter(u => !u.isMe),
                ];
              } else {
                // Workspace channel: current user first, then others alphabetically.
                listItems = [...participants].sort((a, b) => {
                  const isMeA = String(a.userId || a.id || a._id) === currentUserId;
                  const isMeB = String(b.userId || b.id || b._id) === currentUserId;
                  if (isMeA) return -1;
                  if (isMeB) return 1;
                  const nameA = (a.name || a.username || a.full_name || '').toLowerCase();
                  const nameB = (b.name || b.username || b.full_name || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                }).map(p => ({
                  id: String(p.userId || p.id || p._id || ''),
                  name: p.name || p.username || p.full_name || (isScenarioChat ? 'Participant' : 'Responder'),
                  role: p.role || p.access_level,
                  profile_image: p.profile_image,
                  isMe: String(p.userId || p.id || p._id) === currentUserId,
                }));
              }

              return listItems.map((p, idx) => {
                const baseName = p.name || 'Member';
                const displayName = p.isMe ? `You (${baseName})` : baseName;
                const initial = (baseName[0] || 'M').toUpperCase();

                return (
                  <div key={p.id || idx} className={`flex items-center justify-between p-3 rounded-xl transition-all duration-200 group ${
                    p.isMe ? 'bg-indigo-50/50 border border-indigo-100' : 'hover:bg-gray-50 border border-transparent'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Avatar className="h-11 w-11 border-2 border-white shadow-sm">
                          <AuthenticatedMedia as={AvatarImage} src={p.profile_image ? getFullUrl(p.profile_image) : null} />
                          <AvatarFallback className={`${p.isMe ? 'bg-indigo-600' : 'bg-gray-400'} text-white font-bold text-sm`}>
                            {initial}
                          </AvatarFallback>
                        </Avatar>
                        {p.isMe && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full" />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className={`font-bold ${p.isMe ? 'text-indigo-900' : 'text-gray-800'}`}>{displayName}</span>
                        <span className="text-[10px] text-gray-500 uppercase tracking-widest font-extrabold flex items-center gap-1.5 mt-0.5">
                          {p.role || (activeConvo?.type === 'group'
                            ? 'Group Member'
                            : (isScenarioChat ? 'Scenario Participant' : 'Active Member'))}
                          {p.isMe && <span className="w-1 h-1 bg-indigo-300 rounded-full" />}
                          {p.isMe && <span className="text-indigo-400 normal-case font-medium">Session Owner</span>}
                        </span>
                      </div>
                    </div>
                    {!p.isMe && canContactUser(p.id) && !channelReadOnly && (
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-x-2 group-hover:translate-x-0">
                        <Button
                          size="icon" variant="ghost"
                          className="h-9 w-9 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-full shadow-sm bg-white border border-indigo-50"
                          onClick={() => handleDirectCall({ id: p.id, name: baseName }, 'voice')}
                        >
                          <Phone size={16} />
                        </Button>
                        <Button
                          size="icon" variant="ghost"
                          className="h-9 w-9 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-full shadow-sm bg-white border border-indigo-50"
                          onClick={() => handleDirectCall({ id: p.id, name: baseName }, 'video')}
                        >
                          <Video size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
          {!activeConvo && isAdmin && activeChannel && (
            <div className="pt-4 border-t border-gray-100 mt-2">
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 font-bold shadow-lg shadow-indigo-200"
                onClick={() => {
                  setIsParticipantsListOpen(false);
                  setChannelManager({ open: true, channel: activeChannel, initialUserIds: [] });
                }}
              >
                <Settings size={18} className="mr-2" />
                Manage Channel Membership
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sidebar DM modal — triggered by clicking a conversation in the Chat Lists panel */}
    </div>
  );
};

export default Chat;
